/**
 * Note Section Save Guard Hook
 *
 * Prevents navigation when there are pending section saves to avoid losing user edits.
 * Automatically waits for saves to complete before allowing navigation.
 * Uses React Router's useBlocker and browser beforeunload event.
 *
 * Tracks two states:
 * - Pending saves: API calls in-flight (blocks navigation until complete)
 * - Dirty sections: Content modified but not yet saved (warns on tab close)
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useBlocker } from 'react-router-dom';

export interface UseNoteSectionSaveGuardReturn {
  /**
   * Call this when a save operation starts, passing the section ID and save promise
   */
  onSaveStart: (sectionId: string, savePromise: Promise<void>) => void;

  /**
   * Call this when a section's content changes (marks it as dirty)
   */
  markDirty: (sectionId: string) => void;

  /**
   * Whether there are currently pending saves
   */
  hasPendingSaves: boolean;

  /**
   * Whether there are unsaved changes (dirty sections)
   */
  hasUnsavedChanges: boolean;
}

/**
 * Hook to guard navigation when note sections have pending saves
 *
 * When navigation is attempted with pending saves, waits for all saves to complete
 * before automatically proceeding with navigation (no user confirmation needed).
 *
 * When browser tab is closed with unsaved changes, warns the user.
 *
 * @returns Object with callbacks to track save state and dirty state
 */
export function useNoteSectionSaveGuard(): UseNoteSectionSaveGuardReturn {
  // Track pending save promises
  const pendingSavesRef = useRef<Set<Promise<void>>>(new Set());
  const [pendingSaveCount, setPendingSaveCount] = useState(0);
  const hasPendingSaves = pendingSaveCount > 0;

  // Track dirty sections (content modified but not saved)
  const dirtySectionsRef = useRef<Set<string>>(new Set());
  const [dirtySectionCount, setDirtySectionCount] = useState(0);
  const hasUnsavedChanges = dirtySectionCount > 0;

  // Block navigation when there are pending saves
  const blocker = useBlocker(({ currentLocation, nextLocation }) => {
    // Only block if there are pending saves and navigating to a different path
    return pendingSavesRef.current.size > 0 && currentLocation.pathname !== nextLocation.pathname;
  });

  // Handle blocked navigation - wait for saves then auto-proceed
  useEffect(() => {
    if (blocker.state === 'blocked') {
      const waitAndProceed = async () => {
        // Keep waiting until all saves complete, including any new ones that start
        // during the wait (e.g., blur-triggered save from the navigation click)
        // Safety limit prevents infinite loops if saves keep being added
        let attempts = 0;
        const maxAttempts = 10;
        while (attempts < maxAttempts) {
          const currentSaves = Array.from(pendingSavesRef.current);
          if (currentSaves.length === 0) break;
          try {
            await Promise.all(currentSaves);
          } catch (error) {
            // Even if some saves fail, continue checking for more pending saves
            // The error was already handled in the component
            console.warn('[NoteSectionSaveGuard] Some saves failed, continuing to wait:', error);
          }
          attempts++;
        }
        // All saves complete (or max attempts reached), proceed with navigation
        blocker.proceed();
      };
      waitAndProceed();
    }
  }, [blocker]);

  // Browser beforeunload event for closing tab/window
  // Warns if there are unsaved changes (dirty sections)
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      // Warn if there are dirty sections or pending saves
      if (dirtySectionsRef.current.size > 0 || pendingSavesRef.current.size > 0) {
        e.preventDefault();
        // eslint-disable-next-line @typescript-eslint/no-deprecated
        e.returnValue = ''; // Chrome requires returnValue to be set
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  // Mark a section as dirty (content has been modified)
  const markDirty = useCallback((sectionId: string) => {
    dirtySectionsRef.current.add(sectionId);
    setDirtySectionCount(dirtySectionsRef.current.size);
  }, []);

  // Callback to track save operations
  const onSaveStart = useCallback((sectionId: string, savePromise: Promise<void>) => {
    pendingSavesRef.current.add(savePromise);
    setPendingSaveCount(pendingSavesRef.current.size);

    // Remove promise and clear dirty state when save completes
    savePromise.finally(() => {
      pendingSavesRef.current.delete(savePromise);
      setPendingSaveCount(pendingSavesRef.current.size);

      // Clear dirty state for this section (save completed)
      dirtySectionsRef.current.delete(sectionId);
      setDirtySectionCount(dirtySectionsRef.current.size);
    });
  }, []);

  return {
    onSaveStart,
    markDirty,
    hasPendingSaves,
    hasUnsavedChanges,
  };
}
